import { Icons } from '@/enums/payments'
import { MemberStatuses, NewMemberStatusesKeys } from '@/enums/member'
import { CountryCodes, NewCountries } from '@/enums/general'
import moment from 'moment'
import icons from '@/assets/icons'
import { datadogRum } from '@datadog/browser-rum'

/**
 * Capitalizes a word
 *
 * @param word
 * @returns {string}
 */
export const capitalize = (word = '') => {
  if (typeof word !== 'string' || word.length === 0) {
    return ''
  }

  const letters = word.split('')
  return letters[0].toUpperCase() + letters.slice(1).join('')
}

/**
 * Convert image object to the right format for the srcSet attribute
 *
 * @param image
 * @param s3
 * @returns {{desktop: string, mobile: string}}
 */
export const convertSrcSet = (image, s3 = false) => {
  const result = {
    desktop: '',
    mobile: '',
    src: '',
    alt: ''
  }

  const runtimeConfig = useRuntimeConfig()

  const basePath = s3 ? runtimeConfig.public.BASE_S3_PATH : ''

  if (!Object.prototype.hasOwnProperty.call(image, 'desktop') && !Object.prototype.hasOwnProperty.call(image, 'mobile')) {
    return result
  }

  const desktop = Object.prototype.hasOwnProperty.call(image, 'desktop') ? 'desktop' : 'mobile'
  const mobile = Object.prototype.hasOwnProperty.call(image, 'mobile') ? 'mobile' : 'desktop'

  return {
    desktop: `${basePath}${image[desktop]['1x']},  ${basePath}${image[desktop]['2x']} 2x`.toLowerCase(),
    mobile: `${basePath}${image[mobile]['1x']},  ${basePath}${image[mobile]['2x']} 2x`.toLowerCase(),
    src: `${basePath + image[mobile]['1x']}`.toLowerCase()
  }
}

/**
 * Convert a single source to the right format
 *
 * @param {string} path
 * @param {boolean} s3
 * @param {any} runtimeConfig - Optional runtime config (for SSR compatibility)
 * @returns {string}
 */
export const convertSrc = (path = '', s3 = false, runtimeConfig = null) => {
  // Use provided config or try to get it (may fail in SSR if called outside proper context)
  let config = runtimeConfig
  if (!config) {
    try {
      config = useRuntimeConfig()
    } catch {
      // useRuntimeConfig not available in this context (e.g., SSR outside proper context)
      config = null
    }
  }
  const basePath = s3 && config ? config.public.BASE_S3_PATH : ''

  return `${basePath}${path}`.toLowerCase()
}

/**
 * Returns the string for a numeric member status.
 *
 * @param value
 * @returns {string}
 */
export const getMemberStatus = (value) => {
  let status = 'Inactive'

  switch (value) {
    case MemberStatuses.enabled:
    case NewMemberStatusesKeys.active:
      status = 'Active'
      break
    case MemberStatuses.new:
    case NewMemberStatusesKeys.trial_period:
      status = 'Trial'
      break
    case MemberStatuses.paused:
    case NewMemberStatusesKeys.paused:
      status = 'Paused'
      break
  }

  return status
}

/**
 * Returns the string for a numeric payment status.
 *
 * @param value
 * @returns {string}
 */
export const getPaymentStatus = (value) => {
  let status = 'Waste'

  switch (value) {
    case 0:
      status = 'Open'
      break
    case 1:
    case 7:
      status = 'Paid'
      break
    case 2:
      status = 'Failed'
      break
    case 3:
      status = 'ChargeBack'
      break
    case 4:
      status = 'LatePayment'
      break
    case 5:
      status = 'Pending'
      break
    case 6:
      status = 'Refund'
      break
  }

  return status
}

/**
 * Returns a array with icons per payment method
 *
 * @param paymentMethod string
 * @returns {array[]}
 */
export const getPaymentIcon = (paymentMethod) => {
  return Icons[paymentMethod.toLowerCase()]
}

/**
 * Returns uppercase initials from fullname.
 *
 * @param fullName
 * @returns {*}
 */
export const getInitials = (fullName) => {
  const names = fullName.split(' ')

  const firstLetter = names[0].charAt(0)
  const lastLetter = names[names.length - 1].charAt(0)

  return (firstLetter + lastLetter).toUpperCase()
}

/**
 * Toggle modal visibility.
 *
 * @param modal
 * @param open
 */
export const togglePageScroll = (open = true) => {
  if (import.meta.client) {
    try {
      const body = document.querySelector('body')
      if (open) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
        body.style.paddingRight = `${scrollbarWidth}px`
        body.classList.add('overflow-hidden')
      } else {
        body.style.paddingRight = ''
        body.classList.remove('overflow-hidden')
      }
    } catch (_error) {
      // noop
    }
  }
}

/**
 * Format date to YYYY-MM-DD (H:i:s) string.
 *
 * @param date
 * @param includeTime
 * @returns {string}
 */
export const formatDateToString = (date, includeTime = false) => {
  const d = new Date(date)
  let month = '' + (d.getMonth() + 1)
  let day = '' + d.getDate()
  const year = d.getFullYear()

  if (month.length < 2) {
    month = '0' + month
  }
  if (day.length < 2) {
    day = '0' + day
  }

  let dateString = [year, month, day].join('-')

  if (includeTime) {
    let hours = '' + d.getHours()
    let minutes = '' + d.getMinutes()
    let seconds = '' + d.getSeconds()

    if (hours.length < 2) {
      hours = '0' + hours
    }
    if (minutes.length < 2) {
      minutes = '0' + minutes
    }
    if (seconds.length < 2) {
      seconds = '0' + seconds
    }

    dateString = dateString + ' ' + [hours, minutes, seconds].join(':')
  }

  return dateString
}

/*
 * Gets the hash value from the url
 */
export const getUrlHash = () => {
  if (import.meta.client) {
    let currentHash = window.location.hash

    if (currentHash.includes('?')) {
      currentHash = currentHash.split('?')[0]
    }

    return currentHash.replace('#', '')
  }

  return ''
}

/**
 * Adds a hash to the url
 *
 * @param hash
 */
export const setUrlHash = (hash) => {
  if (import.meta.client) {
    window.location.hash = hash
  }
}

/**
 * Logs error to bug tracking tool
 *
 * @param error
 * @param context
 */
export const logError = (error, context = null) => {
  if (import.meta.client && window) {
    datadogRum.addError(error, context)
  }
}

/**
 * gets the image paths
 *
 * @param path
 * @param s3
 */
export const getImagePaths = ({
  path = '',
  extension = '.webp',
  s3 = false,
  noDesktop = false,
  noMobile = false,
  mobilePostFix = '-mob',
  retinaPostFix = '@2x'
}) => {
  return convertSrcSet(
    {
      ...(noDesktop
        ? {}
        : {
            desktop: {
              '1x': `${path}${extension}`,
              '2x': `${path}${retinaPostFix}${extension}`
            }
          }),
      ...(noMobile
        ? {}
        : {
            mobile: {
              '1x': `${path}${mobilePostFix}${extension}`,
              '2x': `${path}${mobilePostFix}${retinaPostFix}${extension}`
            }
          })
    },
    s3
  )
}

/**
 * Gets custom error messages
 *
 * @param error
 */
export const getCustomErrorMessages = ({ graphQLErrors }) => {
  if (graphQLErrors && graphQLErrors.length) {
    const graphQLErrorObject = graphQLErrors.find((error) => Object.prototype.hasOwnProperty.call(error, 'messages'))

    if (graphQLErrorObject) {
      return graphQLErrorObject
    }
  }

  return {}
}

/**
 * Gets custom error messages
 *
 * @param error
 */
export const getRESTCustomErrorMessages = (violations) => {
  if (violations && violations.length) {
    return violations.reduce(
      (acc, { propertyPath, messageKey }) => ({
        ...acc,
        [propertyPath]: messageKey
      }),
      {}
    )
  }

  return null
}

/**
 * Gets custom error messages for V2 API
 *
 */
export const getRESTV2CustomErrorMessages = (error) => {
  let populatedErrors = null

  if (error && error.response?.data?.violations) {
    populatedErrors = error.response.data.violations.reduce(
      (acc, { propertyPath, messageKey, message, title, template }) => ({
        ...acc,
        [propertyPath]: {
          locizeKey: `Api.${messageKey || template}`,
          propertyPath,
          message: message || title
        }
      }),
      {}
    )
  }

  if (error && error.response?.data?.error && error.response?.data?.errorKey) {
    populatedErrors = {
      ...(populatedErrors || {}),
      general: {
        locizeKey: `Api.${error.response.data.errorKey}`,
        message: error.response.data.error,
        propertyPath: 'general'
      }
    }
  }

  return populatedErrors
}

/**
 * Formats a number
 * example 10000 -> 10.000
 *
 * @param number
 */
export const formatNumber = (number = 0) => {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
}

export const isFreeSocksCampaign = (country) => {
  const allowedCountries = [
    CountryCodes.austria,
    CountryCodes.belgium,
    CountryCodes.denmark,
    CountryCodes.germany,
    CountryCodes.spain,
    CountryCodes.france,
    CountryCodes.netherlands,
    CountryCodes.unitedKingdom,
    CountryCodes.sweden
  ]

  const showFromDate = moment('28-04-2022', 'DD-MM-YYYY')
  const showToDate = moment('04-05-2043', 'DD-MM-YYYY')

  const isFreeSocksCampaign = moment().isBetween(showFromDate, showToDate)

  return isFreeSocksCampaign && allowedCountries.includes(country)
}

export const getCorrectedDeliveryDates = (design = '', _country) => {
  const designInLowerCase = design.toLowerCase()

  switch (designInLowerCase) {
    case 'heroes':
      return {
        fromDate: moment('15-09-2023', 'DD-MM-YYYY'),
        tillDate: moment('23-09-2023', 'DD-MM-YYYY')
      }
    case 'sixtie':
      return {
        fromDate: moment('17-05-2024', 'DD-MM-YYYY'),
        tillDate: moment('23-05-2024', 'DD-MM-YYYY')
      }
  }

  return null
}

export const getCorrectReturnAfterDay = (design = '', country) => {
  const designInLowerCase = design.toLowerCase()

  switch (designInLowerCase) {
    case 'beau':
      if ([CountryCodes.netherlands, CountryCodes.austria, CountryCodes.spain, CountryCodes.sweden].includes(country)) {
        return moment('19-06-2022', 'DD-MM-YYYY').format('LL')
      } else if ([CountryCodes.belgium, CountryCodes.germany].includes(country)) {
        return moment('17-06-2022', 'DD-MM-YYYY').format('LL')
      } else {
        return moment('18-06-2022', 'DD-MM-YYYY').format('LL')
      }
  }

  switch (country) {
    case CountryCodes.austria:
      return 'the 25th of this month'
    case CountryCodes.belgium:
      return 'de 20ste'
    case CountryCodes.denmark:
      return 'the 25th of this month'
    case CountryCodes.germany:
      return 'the 25th of this month'
    case CountryCodes.spain:
      return 'the 25th of this month'
    case CountryCodes.france:
      return 'the 25th of this month'
    case CountryCodes.netherlands:
      return 'de 20ste'
    case CountryCodes.unitedKingdom:
      return 'the 25th of this month'
    case CountryCodes.sweden:
      return 'the 25th of this month'
  }

  return moment(new Date()).add(14, 'day').format('LL')
}

export const serializeParameters = (obj = {}) => {
  const str = []
  const keys = Object.keys(obj)
  keys.forEach((key) => {
    if (obj[key]) {
      str.push(`${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`)
    }
  })
  return str.join('&')
}

export const stripLanguageRegex = new RegExp('^\\/[a-z]{2}-[a-z]{2}/') // strips any language from the path (like: '/nl-nl/' or '/de-nl/')

export const isFeaturedBoxersCampaign = () => {
  const showFromDate = moment('12-10-2023', 'DD-MM-YYYY')
  const showToDate = moment('01-11-2023', 'DD-MM-YYYY')
  const showCampaign = moment().isBetween(showFromDate, showToDate)

  return showCampaign
}

export const isNewCountry = (country = 'nl') => {
  return NewCountries.includes(country)
}

export const isValidSVGIcon = (svg = '') => icons.includes(svg)

export const getCurrencyIcon = (currency = 'euro') => {
  let icon = '€'

  switch (currency) {
    case 'euro':
      icon = '€'
      break
    case 'gbp':
      icon = '£'
      break
    case 'pln':
      icon = 'zł'
      break
    case 'chf':
      icon = 'Fr.'
      break
    case 'nok':
      icon = 'kr'
      break
    case 'czk':
      icon = 'Kč'
      break
    case 'dkk':
    case 'sek':
      icon = 'kr.'
  }

  return icon
}

export const extractValueBetweenBrackets = (str) => {
  const match = str.match(/\((.*?)\)/)

  return match ? match[1] : null
}

// Seeded random number generator for consistent SSR/client results
class SeededRandom {
  constructor(seed) {
    this.seed = seed
  }

  next() {
    this.seed = (this.seed * 9301 + 49297) % 233280
    return this.seed / 233280
  }
}

export const getRandomIndexes = (array, count, seed = null) => {
  if (array.length === 0) {
    return [0, 0, 0]
  }

  if (count > array.length) {
    return [0, 0, 0]
  }

  const indexes = []

  // Use seeded random if seed is provided, otherwise fall back to Math.random()
  const random = seed !== null ? new SeededRandom(seed) : null

  while (indexes.length < count) {
    const randomValue = random ? random.next() : Math.random()
    const index = Math.floor(randomValue * array.length)
    if (!indexes.includes(index)) {
      indexes.push(index)
    }
  }

  return indexes
}

export const stripWebsiteAndLocaleAndCountry = (url) => {
  // Match the locale pattern and everything before it
  const regex = /^(?:https?:\/\/)?(?:[^/]+)?\/[a-z]{2}-[a-z]{2}/i

  // Replace the matched pattern with empty string and ensure starts with /
  const strippedPath = url.replace(regex, '')

  // If the path is empty, return /, otherwise return the stripped path
  return strippedPath || '/'
}
